import {
  Card,
  CardContent,
  Skeleton,
  Box,
  Grid,
  Container,
  IconButton,
  Button,
  LinearProgress,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const StyledSkeletonCard = styled(Card)(({ theme }) => ({
  borderRadius: 0,
  background: theme.palette.background.paper,
  border: "none",
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 18px 40px rgba(0,0,0,0.22)"
      : "0 18px 40px rgba(15,23,42,0.06)",
  height: "440px",
  width: "100%",
  maxWidth: "100%",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
}));

const HeaderWithButton = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  width: "100%",
}));

const CleanButton = styled(Button)(({ theme }) => ({
  variant: "outlined",
  borderRadius: "35px",
  padding: "8px 24px",
  fontSize: "1rem",
  fontWeight: 600,
  backgroundColor: "transparent",
  color:
    theme.palette.mode === "light"
      ? theme.palette.background.accent
      : theme.palette.primary.main,
  border: `2px solid ${
    theme.palette.mode === "light"
      ? theme.palette.background.accent
      : theme.palette.primary.main
  }`,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.04)"
        : theme.palette.primary.main,
    color:
      theme.palette.mode === "light"
        ? theme.palette.background.accent
        : "white",
    transform: "translateY(-2px)",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
}));

const ProfileCardSkeleton = ({ onNewSearch }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [progress, setProgress] = useState(0);

  // Realistic enrichment timing: v2 resolve queue (3-45s) + v1 /profile
  // hydration with retry through 504 storms (5-50s) + OpenAI salary
  // estimation (5-15s). Worst case ~90s, typical ~6-15s for cached/url
  // flows. tau=14000 → 60% at ~14s, 86% at ~28s, 95% at ~42s — bar
  // keeps moving throughout most real searches and rarely idles at 95%.
  useEffect(() => {
    const intervalTime = 120;
    const targetProgress = 95;
    const tau = 14000;
    const startedAt = Date.now();

    const timer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const eased = targetProgress * (1 - Math.exp(-elapsed / tau));
      setProgress((prev) => (eased > prev ? eased : prev));
      if (eased >= targetProgress - 0.5) {
        clearInterval(timer);
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  const chipWidths = [120, 170, 140];

  return (
    <Container
      maxWidth="lg"
      sx={{ py: { xs: 1, md: 4 }, minHeight: "calc(100dvh - 150px)" }}
    >
      <Box sx={{ mt: { xs: 0, md: 8 }, mb: { xs: 1, md: 4 } }}>
        <HeaderWithButton>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          ></Box>
          {isMobile ? (
            <IconButton
              onClick={onNewSearch}
              sx={{
                backgroundColor: "transparent",
                width: 48,
                height: 48,
                marginTop: "-4px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              <SearchIcon />
            </IconButton>
          ) : (
            <CleanButton onClick={onNewSearch} startIcon={<ClearIcon />}>
              New Search
            </CleanButton>
          )}
        </HeaderWithButton>
      </Box>

      <Grid container spacing={{ xs: 1.5, md: 3 }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Box>
            <StyledSkeletonCard>
              <CardContent
                sx={{
                  p: 3,
                  mb: 1,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {/* Avatar and User Info Section */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Skeleton
                    variant="circular"
                    width={80}
                    height={80}
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={32}
                      sx={{ mb: 0 }}
                    />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Skeleton
                        variant="text"
                        width="30%"
                        height={18}
                        sx={{ mr: 1 }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Pricing and Response Rate Section */}
                <Box sx={{ mt: 2, mb: 3, flex: 1 }}>
                  <Box sx={{ mb: 3, flex: 1 }}>
                    <Box sx={{ mt: 0.5, display: "flex", gap: 1 }}>
                      <Skeleton variant="text" width="35%" height={20} />
                    </Box>
                    <Box sx={{ mt: 0.5, display: "flex", gap: 1 }}>
                      <Skeleton variant="text" width="45%" height={20} />
                    </Box>
                  </Box>

                  {/* Skills/Expertise Chips - Changed to vertical layout */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {chipWidths.map((width, idx) => (
                      <Skeleton
                        key={idx}
                        variant="rounded"
                        width={width}
                        height={21}
                        sx={{ borderRadius: "12px" }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Button Section */}
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={40}
                  sx={{ borderRadius: "35px" }}
                />
              </CardContent>
              <Skeleton
                variant="text"
                width="70%"
                height={16}
                sx={{
                  mt: 1,
                  mx: "auto",
                  mb: 1,
                  textAlign: "center",
                }}
              />
            </StyledSkeletonCard>

            {/* Progress Bar and Text */}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  width: "94%",
                  mb: 1,
                  ml: "3%",
                  borderRadius: 1,
                  height: 4,
                  backgroundColor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: theme.palette.grey[500],
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  fontFamily: "monospace",
                }}
              >
                We&apos;re scanning 50+ data sources...{" "}
                {Math.round(progress).toString().padStart(2, "0")}%{" "}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfileCardSkeleton;
